import React, { useContext, useEffect, useRef } from 'react';
import { MobXProviderContext } from 'mobx-react';

import Guideline from '../tools/Guideline';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import '../../../css/BookmarkActivity.scss';

import AuthService from '../../services/AuthService';
import lessonService from '../../services/LessonService';

import DirectionsFlyout from '../tabbedFlyout/DirectionsFlyout';
import useStyleEvents from '../../../hooks/useStyleEvents';

const BookmarkActivity = ({ lessonElementId }) => {
  const {
    lessonManager,
    toolbarManager,
    docReaderManager,
    studentActivityManager,
  } = useContext(MobXProviderContext);

  const model = lessonManager.getSlideModel(lessonElementId);
  // console.log('model', model);
  let url = model.pageUrl;
  const isPDF = (model.entityTypeId === 'pdf_resource');
  const isDocReader = isPDF && model.pdfViewerTool === 'docreader';

  if (isPDF && !isDocReader) {
    url = AuthService.getFlowpaperUrl(model);
  } else if (url) {
    if (AuthService.isGoogleDoc(url)) {
      url = AuthService.fixGoogleDocUrl(url);
    }
  }

  // Only fetch DocReader URL when contentItemId changes to prevent
  // iframe reload when switching questions within the BookmarkActivity
  useEffect(() => {
    if (isDocReader && model.contentItemId) {
      lessonService.fetchDocReaderUrl(model.contentItemId, null)
        .then((docReaderUrl) => {
          if (docReaderUrl) {
            const url = lessonManager.docReaderUrl;
            const iframe = document.getElementById(`${lessonElementId}-webpage-iframe`);
            if (iframe && iframe.src !== url) {
              iframe.src = url;
            }
          }
        })
        .catch((error) => console.error('Error fetching doc reader URL:', error));
    }
  }, [model.contentItemId, isDocReader]);

  useStyleEvents(lessonElementId, `${lessonElementId}-activity-view`);

  const backRef = useRef();
  const forwardRef = useRef();
  // Add refs for the Highlights and Annotations buttons
  const highlightBtnRef = useRef();
  const annotationBtnRef = useRef();

  useAccessibilityClick(backRef, (event) => {
    // ensure 'back' button is unfocused upon click
    event?.target?.blur?.();

    const element = document.getElementById(`${lessonElementId}-webpage-iframe`);
    if (element && window.history.state !== null) {
      window.history.back();
    }
  });

  useAccessibilityClick(forwardRef, (event) => {
    // ensure 'forward' button is unfocused upon click
    event?.target?.blur?.();

    const element = document.getElementById(`${lessonElementId}-webpage-iframe`);
    if (element) {
      window.history.forward();
    }
  });

  useAccessibilityClick(highlightBtnRef, (event) => {
    // ensure 'forward' button is unfocused upon click
    event?.target?.blur?.();
    docReaderManager.activateHighlightTool();
  });

  useAccessibilityClick(annotationBtnRef, (event) => {
    // ensure 'forward' button is unfocused upon click
    event?.target?.blur?.();
    docReaderManager.activateAnnotationTool();
  });

  useEffect(() => {
    window.history.pushState({ name: 'Stop' }, 'stop');
  }, []);

  // let activityId = (studentActivityManager && studentActivityManager.studentActivityId)? studentActivityManager.studentActivityId : '';

  useEffect(() => {
    const iframe = document.getElementById(`${lessonElementId}-webpage-iframe`);

    if (iframe && model && model.pageUrl) {
      iframe.addEventListener('load', () => {
        if (studentActivityManager && studentActivityManager.studentActivityId) {
          // console.log('activity_id', studentActivityManager.studentActivityId);
          // eslint-disable-next-line prefer-template
          const domain = model.pageUrl.split('//')[0] + '//' + model.pageUrl.split('//')[1].split('/')[0];
          iframe.contentWindow.postMessage({ activity_id: studentActivityManager.studentActivityId }, domain);
        }
      });
    }
  }, [lessonElementId, model.pageUrl]);

  const initializeDocReaderFrame = async (id) => {
    docReaderManager.setDocumentSessionId(lessonElementId);
    docReaderManager.initializeDocReaderFrame(id);
    docReaderManager.setDocReaderFrameOwnerLessonElementId(lessonElementId);
  };

  return (
    <div className='bookmark-activity-question' id={`${lessonElementId}-activity-view`}>
      {(model.directions) ? <DirectionsFlyout directions={model.directions} isActivity={true} lessonElementId={model.lessonElementId} /> : ''}
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      {(!isPDF && model.showNavigationButtons) && (
        <div className='web-buttons'>
          <button ref={backRef} className='primary'>Back</button><button ref={forwardRef} className='primary'>Forward</button>
        </div>
      )}
      <div className='test-item-question lesson-element bookmark-question' id={`${lessonElementId}-bookmark-view`}>
        {isDocReader && docReaderManager.canEdit() && (
          <div className='bookmark-toolbar'>
            <button
              ref={highlightBtnRef}
              className='bookmark-toolbar-btn'
            >
              Highlights
            </button>
            <button
              ref={annotationBtnRef}
              className='bookmark-toolbar-btn'
            >
              Annotations
            </button>
          </div>
        )}
        <iframe key={lessonElementId} allowFullScreen={true} id={`${lessonElementId}-webpage-iframe`}
          onLoad={() => {
            if (isDocReader) {
              initializeDocReaderFrame(`${lessonElementId}-webpage-iframe`);
            }
          }}
          src={url} title={model.title} />
      </div>
    </div>
  );
};

export default BookmarkActivity;
